/**
 * Spatial WebRTC media for the office.
 * Mesh topology: each client peers with every other (fine for small HQ teams).
 * Audio is mixed through Web Audio gain nodes so volume tracks world distance.
 *
 * Session phases (see docs/design/office-av-webrtc-fix.md):
 *  A pre-start  — buffer peers/signals; never construct RTCPeerConnection
 *  B graph-ready — audio graph up, gUM in flight; create/answer PC without local tracks
 *  C media-ready — localStream settled; syncLocalTracksToAllPeers
 *
 * ICE recovery (PR4): on connectionState "failed", tear down the PC and silently
 * re-ensure with exponential backoff if the peer is still present. No user toast/UI.
 */

import { logger } from "@/lib/logger";

export type SpatialVolumeFn = (peerId: string) => number;

/** Default public STUN — also returned by GET /api/office/ice when no env override. */
export const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

type MediaPhase = "pre-start" | "graph-ready" | "media-ready";

type PeerMedia = {
  pc: RTCPeerConnection;
  audioGain: GainNode;
  audioSource: MediaStreamAudioSourceNode | null;
  remoteStream: MediaStream;
  videoEl: HTMLVideoElement | null;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
};

export type SpatialMediaCallbacks = {
  onRemoteStream?: (peerId: string, stream: MediaStream) => void;
  onRemoteStreamGone?: (peerId: string) => void;
  /** Fired when local screen-share starts or stops (including browser chrome stop). */
  onSharingChange?: (sharing: boolean) => void;
  /** Fired when local stream composition changes (share start/stop / re-acquire). */
  onLocalStreamChange?: (stream: MediaStream | null) => void;
  sendSignal: (to: string, data: unknown) => void;
  /**
   * Return true if this peer is still in the office and media should be
   * re-established after ICE failure. Hook wires this to peersRef.
   */
  isPeerPresent?: (peerId: string) => boolean;
};

/** Injectable factories for unit tests (deferred gUM, mock PC, AudioContext). */
export type SpatialMediaDeps = {
  createPeerConnection?: (config: RTCConfiguration) => RTCPeerConnection;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  getDisplayMedia?: (constraints: DisplayMediaStreamOptions) => Promise<MediaStream>;
  createAudioContext?: () => AudioContext;
  createMediaStream?: (tracks?: MediaStreamTrack[]) => MediaStream;
  /** ICE servers for new peer connections (defaults to DEFAULT_ICE_SERVERS). */
  iceServers?: RTCIceServer[];
  /** Injectable timers for deterministic ICE-retry tests. */
  setTimeout?: (fn: () => void, ms: number) => number;
  clearTimeout?: (id: number) => void;
};

type BufferedSignal = {
  from: string;
  data: unknown;
  enqueuedAt: number;
};

export class SpatialMediaSession {
  private localStream: MediaStream | null = null;
  private peers = new Map<string, PeerMedia>();
  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private selfId = "";
  private volumeOf: SpatialVolumeFn = () => 0;
  private raf = 0;
  private micEnabled = true;
  private camEnabled = true;
  private iceServers: RTCIceServer[] = DEFAULT_ICE_SERVERS;

  private phase: MediaPhase = "pre-start";
  private pendingPeerIds = new Set<string>();
  /** Only used in State A. Max 64; drop oldest if exceeded. */
  private signalBuffer: BufferedSignal[] = [];
  private static readonly SIGNAL_BUFFER_MAX = 64;
  private static readonly SIGNAL_BUFFER_MAX_AGE_MS = 30_000;

  /** Backoff delays for silent ICE re-ensure (1s, 2s, 4s → max 3 attempts). */
  private static readonly ICE_RETRY_BACKOFF_MS = [1000, 2000, 4000] as const;
  private iceRetryAttempts = new Map<string, number>();
  private iceRetryTimers = new Map<string, number>();

  /** Active display-capture track while screen sharing (replaces camera on senders). */
  private screenTrack: MediaStreamTrack | null = null;
  /** Camera track parked while sharing so we can restore without re-prompt when possible. */
  private parkedCameraTrack: MediaStreamTrack | null = null;
  private sharing = false;

  /**
   * Monotonic generation for start/stop races.
   * Each accepted `start()` and every `stop()` bumps the epoch so a stale
   * `await getUserMedia` continuation never wins over a later start.
   */
  private startEpoch = 0;

  private readonly onScreenTrackEnded = (): void => {
    void this.stopScreenShare();
  };

  constructor(
    private readonly cb: SpatialMediaCallbacks,
    private readonly deps: SpatialMediaDeps = {},
  ) {
    if (deps.iceServers && deps.iceServers.length > 0) {
      this.iceServers = deps.iceServers;
    }
  }

  /** Test/observability: current lifecycle phase. */
  getPhase(): MediaPhase {
    return this.phase;
  }

  /** Replace ICE servers used for subsequently created peer connections. */
  setIceServers(servers: RTCIceServer[]): void {
    if (servers.length > 0) {
      this.iceServers = servers;
    }
  }

  private scheduleTimeout(fn: () => void, ms: number): number {
    const st =
      this.deps.setTimeout ??
      ((cb: () => void, delay: number) => globalThis.setTimeout(cb, delay) as unknown as number);
    return st(fn, ms);
  }

  private cancelTimeout(id: number): void {
    const ct = this.deps.clearTimeout ?? ((timer: number) => globalThis.clearTimeout(timer));
    ct(id);
  }

  private createPc(config: RTCConfiguration): RTCPeerConnection {
    return this.deps.createPeerConnection?.(config) ?? new RTCPeerConnection(config);
  }

  private createStream(tracks?: MediaStreamTrack[]): MediaStream {
    if (this.deps.createMediaStream) {
      return this.deps.createMediaStream(tracks);
    }
    return tracks ? new MediaStream(tracks) : new MediaStream();
  }

  private async requestUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
    const gum = this.deps.getUserMedia ?? ((c) => navigator.mediaDevices.getUserMedia(c));
    return gum(constraints);
  }

  private async requestDisplayMedia(constraints: DisplayMediaStreamOptions): Promise<MediaStream> {
    const gdm =
      this.deps.getDisplayMedia ??
      ((c: DisplayMediaStreamOptions) => navigator.mediaDevices.getDisplayMedia(c));
    return gdm(constraints);
  }

  private createAudioContext(): AudioContext {
    return this.deps.createAudioContext?.() ?? new AudioContext();
  }

  private notifyLocalStream(): void {
    this.cb.onLocalStreamChange?.(this.localStream);
  }

  private findVideoSender(pc: RTCPeerConnection): RTCRtpSender | undefined {
    return pc.getSenders().find((s) => s.track?.kind === "video");
  }

  private async replaceVideoTrackOnAllPeers(track: MediaStreamTrack | null): Promise<void> {
    for (const peer of this.peers.values()) {
      const sender = this.findVideoSender(peer.pc);
      if (sender) {
        try {
          await sender.replaceTrack(track);
        } catch (err) {
          logger.warn("webrtc_replace_video_track_failed", {
            errMessage: err instanceof Error ? err.message : String(err),
          });
          // Fall back to addTrack if replace failed and we have a live track.
          if (track && this.localStream) {
            const already = peer.pc.getSenders().some((s) => s.track?.id === track.id);
            if (!already) {
              peer.pc.addTrack(track, this.localStream);
            }
          }
        }
      } else if (track && this.localStream) {
        peer.pc.addTrack(track, this.localStream);
      }
    }
  }

  /** True if this start generation is still the active one and still in State B. */
  private isActiveGraphReady(epoch: number): boolean {
    return this.startEpoch === epoch && this.phase === "graph-ready";
  }

  /** Discard a stream acquired by a stale start generation. */
  private discardStream(stream: MediaStream | null): void {
    if (!stream) return;
    for (const t of stream.getTracks()) t.stop();
    if (this.localStream === stream) {
      this.localStream = null;
    }
  }

  async start(selfId: string, volumeOf: SpatialVolumeFn): Promise<void> {
    if (this.phase !== "pre-start") {
      // Re-entry: hook should not double-start. No-op to keep lifecycle safe.
      return;
    }
    // Own this generation for the whole start() body (including after awaits).
    this.startEpoch += 1;
    const epoch = this.startEpoch;

    this.selfId = selfId;
    this.volumeOf = volumeOf;
    this.audioCtx = this.createAudioContext();
    this.masterGain = this.audioCtx.createGain();
    this.masterGain.connect(this.audioCtx.destination);
    this.phase = "graph-ready"; // State B
    this.tick();

    // Replay State A buffer + create PCs for pending peers (answers proceed during gUM)
    await this.flushPreStartSignalsAndPeers();
    if (!this.isActiveGraphReady(epoch)) return;

    let acquired: MediaStream | null = null;
    try {
      acquired = await this.requestUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: {
          width: { ideal: 320 },
          height: { ideal: 180 },
          frameRate: { ideal: 15 },
        },
      });
    } catch (err) {
      if (!this.isActiveGraphReady(epoch)) return;
      // Mic/cam denied or blocked (no user gesture after auto-enter, Permissions-Policy,
      // or browser deny) — still join for presence; peers get no tracks from us.
      // Callers should re-try ensureLocalMedia on a real user gesture.
      logger.warn("webrtc_start_gum_failed", {
        errName: err instanceof Error ? err.name : "unknown",
        errMessage: err instanceof Error ? err.message : String(err),
      });
      acquired = this.createStream();
    }

    // stop() or a newer start generation raced us — discard media and bail.
    // Phase may already be media-ready if the user started screen share during gUM
    // (read via getPhase so TS doesn't assume phase is still graph-ready after await).
    if (this.startEpoch !== epoch || this.getPhase() === "pre-start") {
      this.discardStream(acquired);
      return;
    }

    if (this.sharing && this.localStream) {
      // Keep the display track; only take audio (and park cam) from gUM.
      for (const t of acquired.getAudioTracks()) {
        if (this.localStream.getAudioTracks().length === 0) {
          this.localStream.addTrack(t);
        } else {
          t.stop();
        }
      }
      for (const t of acquired.getVideoTracks()) {
        if (!this.parkedCameraTrack && t.readyState === "live") {
          this.parkedCameraTrack = t;
        } else {
          t.stop();
        }
      }
    } else {
      this.localStream = acquired;
    }

    this.applyTrackEnabled();
    this.phase = "media-ready"; // State C
    this.syncLocalTracksToAllPeers();
    for (const id of [...this.pendingPeerIds]) {
      this.ensurePeer(id);
    }
    this.pendingPeerIds.clear();
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /** Remote MediaStream for a live peer PC, if any (for late world re-attach). */
  getRemoteStream(peerId: string): MediaStream | null {
    const peer = this.peers.get(peerId);
    return peer ? peer.remoteStream : null;
  }

  setMic(on: boolean): void {
    this.micEnabled = on;
    this.applyTrackEnabled();
  }

  setCam(on: boolean): void {
    this.camEnabled = on;
    this.applyTrackEnabled();
  }

  isSharing(): boolean {
    return this.sharing;
  }

  /**
   * Capture the display and replace the camera video track on every peer.
   * Camera track is parked (not stopped) when possible so stop restores without re-prompt.
   * Returns a status so callers can surface a useful toast (picker cancel vs blocked).
   */
  async startScreenShare(): Promise<"started" | "already" | "cancelled" | "blocked" | "not-ready"> {
    // Screen share only needs a live session graph; camera gUM may still be empty.
    if (this.phase === "pre-start") return "not-ready";
    if (this.sharing) return "already";

    // Prefer simple constraints — over-specified ideals can OverconstrainedError on some browsers.
    const gdmAvailable =
      typeof this.deps.getDisplayMedia === "function" ||
      (typeof navigator !== "undefined" &&
        !!navigator.mediaDevices &&
        typeof navigator.mediaDevices.getDisplayMedia === "function");
    if (!gdmAvailable) {
      logger.warn("webrtc_display_media_unavailable", { reason: "no_getDisplayMedia" });
      return "blocked";
    }

    const epoch = this.startEpoch;
    let acquired: MediaStream | null = null;
    try {
      // Call getDisplayMedia as early as possible (still in user-gesture chain).
      acquired = await this.requestDisplayMedia({
        video: true,
        audio: false,
      });
    } catch (err) {
      const name = err instanceof Error ? err.name : "unknown";
      const message = err instanceof Error ? err.message : String(err);
      logger.warn("webrtc_display_media_failed", {
        errName: name,
        errMessage: message,
      });
      // User dismissed the picker, or gesture/permissions blocked capture.
      if (name === "NotAllowedError" || name === "AbortError") {
        // NotAllowedError is also used when Permissions-Policy blocks display-capture.
        // Heuristic: "Permission denied by system" / policy vs user cancel is hard to split.
        if (/policy|permissions policy|display-capture/i.test(message)) {
          return "blocked";
        }
        return "cancelled";
      }
      return "blocked";
    }

    if (this.startEpoch !== epoch || this.getPhase() === "pre-start") {
      this.discardStream(acquired);
      return "not-ready";
    }

    const displayTrack = acquired.getVideoTracks()[0];
    if (!displayTrack) {
      this.discardStream(acquired);
      return "blocked";
    }

    // Prefer detail content-hint so browsers bias encoder for text/UI.
    try {
      displayTrack.contentHint = "detail";
    } catch {
      // contentHint is best-effort
    }

    // Cap share bitrate a bit after capture (constraints are soft post-gDM).
    try {
      await displayTrack.applyConstraints?.({
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 15, max: 30 },
      });
    } catch {
      // applyConstraints is best-effort
    }

    if (!this.localStream) {
      this.localStream = this.createStream();
    }

    // Park existing camera video track(s); only keep the first for restore.
    for (const t of [...this.localStream.getVideoTracks()]) {
      this.localStream.removeTrack(t);
      if (!this.parkedCameraTrack && t.readyState === "live" && t !== displayTrack) {
        this.parkedCameraTrack = t;
      } else if (t !== displayTrack) {
        t.stop();
      }
    }

    this.screenTrack = displayTrack;
    this.localStream.addTrack(displayTrack);
    this.sharing = true;
    // Promote graph-ready → media-ready once we have something to publish.
    if (this.phase === "graph-ready") {
      this.phase = "media-ready";
    }
    displayTrack.addEventListener("ended", this.onScreenTrackEnded);

    await this.replaceVideoTrackOnAllPeers(displayTrack);
    this.applyTrackEnabled();
    this.cb.onSharingChange?.(true);
    this.notifyLocalStream();
    return "started";
  }

  /**
   * Stop display capture and restore the parked (or re-acquired) camera track.
   * Safe to call when not sharing.
   */
  async stopScreenShare(): Promise<void> {
    if (!this.sharing && !this.screenTrack) return;

    const displayTrack = this.screenTrack;
    this.screenTrack = null;
    this.sharing = false;

    if (displayTrack) {
      displayTrack.removeEventListener("ended", this.onScreenTrackEnded);
      try {
        this.localStream?.removeTrack(displayTrack);
      } catch {
        // ignore
      }
      if (displayTrack.readyState === "live") {
        displayTrack.stop();
      }
    }

    let camera = this.parkedCameraTrack;
    this.parkedCameraTrack = null;
    if (camera && camera.readyState !== "live") {
      camera = null;
    }

    // Re-acquire camera if we want video and nothing was parked.
    if (!camera && this.camEnabled && this.phase === "media-ready") {
      const epoch = this.startEpoch;
      try {
        const stream = await this.requestUserMedia({
          video: {
            width: { ideal: 320 },
            height: { ideal: 180 },
            frameRate: { ideal: 15 },
          },
        });
        if (this.startEpoch !== epoch || this.phase !== "media-ready") {
          this.discardStream(stream);
          this.cb.onSharingChange?.(false);
          this.notifyLocalStream();
          return;
        }
        camera = stream.getVideoTracks()[0] ?? null;
        for (const t of stream.getTracks()) {
          if (t !== camera) t.stop();
        }
      } catch (err) {
        logger.warn("webrtc_restore_camera_failed", {
          errMessage: err instanceof Error ? err.message : String(err),
        });
      }
    }

    if (camera && this.localStream) {
      // Avoid duplicate video tracks.
      for (const t of [...this.localStream.getVideoTracks()]) {
        this.localStream.removeTrack(t);
        if (t !== camera) t.stop();
      }
      this.localStream.addTrack(camera);
    }

    await this.replaceVideoTrackOnAllPeers(camera);
    this.applyTrackEnabled();
    this.cb.onSharingChange?.(false);
    this.notifyLocalStream();
  }

  /**
   * Unlock the spatial audio graph after a user gesture (autoplay policy).
   * Safe to call repeatedly; no-ops when the context is missing.
   */
  async resumeAudio(): Promise<void> {
    if (!this.audioCtx) return;
    try {
      await this.audioCtx.resume();
    } catch (err) {
      logger.warn("webrtc_audio_resume_failed", {
        errMessage: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Re-acquire missing local tracks (e.g. after initial gUM denial) when the user
   * enables mic/cam. Only requests kinds that are requested *and* absent, then
   * syncs new tracks onto every peer PC via Perfect Negotiation.
   */
  async ensureLocalMedia(opts: { audio?: boolean; video?: boolean }): Promise<void> {
    if (this.phase !== "media-ready") return;

    // Capture generation before any await so stop()+restart cannot attach
    // stale re-acquire tracks onto a newer media-ready session.
    const epoch = this.startEpoch;

    if (!this.localStream) {
      this.localStream = this.createStream();
    }

    const needAudio = !!opts.audio && this.localStream.getAudioTracks().length === 0;
    // While screen sharing the video m-line carries the display track — don't steal it for cam.
    const needVideo =
      !!opts.video && !this.sharing && this.localStream.getVideoTracks().length === 0;
    if (!needAudio && !needVideo) return;

    const constraints: MediaStreamConstraints = {};
    if (needAudio) {
      constraints.audio = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
    }
    if (needVideo) {
      constraints.video = {
        width: { ideal: 320 },
        height: { ideal: 180 },
        frameRate: { ideal: 15 },
      };
    }

    let acquired: MediaStream | null = null;
    try {
      acquired = await this.requestUserMedia(constraints);
    } catch (err) {
      logger.warn("webrtc_reacquire_media_failed", {
        audio: needAudio,
        video: needVideo,
        errMessage: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    // stop()/restart or phase change while gUM was pending — discard and bail
    if (this.startEpoch !== epoch || this.phase !== "media-ready" || !this.localStream) {
      this.discardStream(acquired);
      return;
    }

    for (const track of acquired.getTracks()) {
      // Avoid duplicate track ids if a concurrent re-acquire already added them.
      const existing =
        track.kind === "audio"
          ? this.localStream.getAudioTracks()
          : this.localStream.getVideoTracks();
      if (existing.length === 0) {
        this.localStream.addTrack(track);
      } else {
        track.stop();
      }
    }

    this.applyTrackEnabled();
    this.syncLocalTracksToAllPeers();
  }

  private applyTrackEnabled(): void {
    if (!this.localStream) return;
    for (const t of this.localStream.getAudioTracks()) t.enabled = this.micEnabled;
    for (const t of this.localStream.getVideoTracks()) {
      // Screen share stays live regardless of cam mute; cam only gates camera tracks.
      if (this.screenTrack && t.id === this.screenTrack.id) {
        t.enabled = true;
      } else {
        t.enabled = this.camEnabled;
      }
    }
    // Parked camera should not publish while sharing (already removed from localStream).
    if (this.parkedCameraTrack && this.parkedCameraTrack.readyState === "live") {
      this.parkedCameraTrack.enabled = this.camEnabled;
    }
  }

  /** Local tracks in stable order: audio first, then video (stable m-line growth). */
  private getLocalTracks(): MediaStreamTrack[] {
    if (!this.localStream) return [];
    return [...this.localStream.getAudioTracks(), ...this.localStream.getVideoTracks()];
  }

  /**
   * Ensure every local track is a sender on this peer.
   * addTrack triggers onnegotiationneeded → Perfect Negotiation renegotiates.
   * Idempotent: skip if a sender already references the same track id.
   */
  private syncLocalTracksToPeer(peer: PeerMedia): void {
    if (!this.localStream) return;
    const senders = peer.pc.getSenders();
    for (const track of this.getLocalTracks()) {
      const already = senders.some((s) => s.track?.id === track.id);
      if (!already) {
        peer.pc.addTrack(track, this.localStream);
      }
    }
    this.applyTrackEnabled();
  }

  private syncLocalTracksToAllPeers(): void {
    for (const peer of this.peers.values()) {
      this.syncLocalTracksToPeer(peer);
    }
  }

  private enqueueSignal(from: string, data: unknown): void {
    this.signalBuffer.push({ from, data, enqueuedAt: Date.now() });
    while (this.signalBuffer.length > SpatialMediaSession.SIGNAL_BUFFER_MAX) {
      this.signalBuffer.shift();
    }
  }

  private async flushPreStartSignalsAndPeers(): Promise<void> {
    const now = Date.now();
    const buffered = this.signalBuffer.filter(
      (s) => now - s.enqueuedAt <= SpatialMediaSession.SIGNAL_BUFFER_MAX_AGE_MS,
    );
    this.signalBuffer = [];

    for (const id of [...this.pendingPeerIds]) {
      this.ensurePeer(id);
    }
    this.pendingPeerIds.clear();

    for (const entry of buffered) {
      // One bad peer must not abort the rest of the serial flush.
      try {
        await this.handleSignal(entry.from, entry.data);
      } catch (err) {
        logger.warn("webrtc_flush_signal_failed", {
          from: entry.from,
          errMessage: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }

  private createPeerMedia(peerId: string): PeerMedia {
    if (!this.audioCtx || !this.masterGain) {
      throw new Error("createPeerMedia called without audio graph (phase invariant)");
    }
    const polite = this.selfId < peerId;
    const pc = this.createPc({ iceServers: this.iceServers });
    const remoteStream = this.createStream();
    const audioGain = this.audioCtx.createGain();
    audioGain.gain.value = 0;
    audioGain.connect(this.masterGain);

    const peer: PeerMedia = {
      pc,
      audioGain,
      audioSource: null,
      remoteStream,
      videoEl: null,
      polite,
      makingOffer: false,
      ignoreOffer: false,
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.cb.sendSignal(peerId, { kind: "ice", candidate: ev.candidate.toJSON() });
      }
    };

    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        // Peer may have been removed while setLocalDescription was in flight.
        if (!this.peers.has(peerId)) return;
        this.cb.sendSignal(peerId, { kind: "sdp", description: pc.localDescription });
      } catch (err) {
        logger.warn("webrtc_negotiation_failed", {
          peerId,
          signalingState: pc.signalingState,
          errMessage: err instanceof Error ? err.message : String(err),
        });
      } finally {
        peer.makingOffer = false;
      }
    };

    pc.ontrack = (ev) => {
      remoteStream.addTrack(ev.track);
      if (ev.track.kind === "audio" && this.audioCtx) {
        if (peer.audioSource) {
          try {
            peer.audioSource.disconnect();
          } catch {
            // ignore disconnect races on teardown
          }
        }
        // Clone stream for Web Audio so the element can also play if needed
        const audioStream = this.createStream([ev.track]);
        peer.audioSource = this.audioCtx.createMediaStreamSource(audioStream);
        peer.audioSource.connect(peer.audioGain);
      }
      this.cb.onRemoteStream?.(peerId, remoteStream);
    };

    pc.onconnectionstatechange = () => {
      // Diagnostics: log every transition (PR4).
      logger.info("webrtc_connection_state", {
        peerId,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
      });
      if (pc.connectionState === "failed") {
        this.handlePeerConnectionFailed(peerId);
      } else if (pc.connectionState === "closed") {
        // Closed without going through failed recovery (remote hangup / our close).
        // removePeer is idempotent; skip if already torn down by failed path.
        if (this.peers.get(peerId)?.pc === pc) {
          this.removePeer(peerId);
        }
      } else if (pc.connectionState === "connected") {
        // Successful media path — clear any prior failure counter for this peer.
        this.iceRetryAttempts.delete(peerId);
      }
    };

    pc.oniceconnectionstatechange = () => {
      logger.info("webrtc_ice_connection_state", {
        peerId,
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
      });
    };

    return peer;
  }

  /**
   * Silent ICE recovery: tear down the dead PC and re-ensure with backoff
   * if the peer is still present. No user-facing toast or reconnect UI.
   */
  private handlePeerConnectionFailed(peerId: string): void {
    const stillPresent = this.cb.isPeerPresent?.(peerId) ?? this.peers.has(peerId);
    // Snapshot attempt count before removePeer clears retry state.
    const attempt = this.iceRetryAttempts.get(peerId) ?? 0;
    this.removePeer(peerId);

    if (!stillPresent) return;

    if (attempt >= SpatialMediaSession.ICE_RETRY_BACKOFF_MS.length) {
      logger.warn("webrtc_ice_recovery_exhausted", {
        peerId,
        attempts: attempt,
      });
      return;
    }

    const delayMs = SpatialMediaSession.ICE_RETRY_BACKOFF_MS[attempt] ?? 4000;
    this.iceRetryAttempts.set(peerId, attempt + 1);

    logger.info("webrtc_ice_recovery_scheduled", {
      peerId,
      attempt: attempt + 1,
      delayMs,
      phase: this.phase,
    });

    const epoch = this.startEpoch;
    const timer = this.scheduleTimeout(() => {
      this.iceRetryTimers.delete(peerId);
      // Session stopped or peer left while waiting.
      if (this.startEpoch !== epoch || this.phase === "pre-start") return;
      const present = this.cb.isPeerPresent?.(peerId) ?? true;
      if (!present) {
        this.clearIceRetry(peerId);
        return;
      }
      logger.info("webrtc_ice_recovery_ensure", {
        peerId,
        attempt: this.iceRetryAttempts.get(peerId) ?? 0,
      });
      this.ensurePeer(peerId);
    }, delayMs);
    this.iceRetryTimers.set(peerId, timer);
  }

  private clearIceRetry(peerId: string): void {
    const timer = this.iceRetryTimers.get(peerId);
    if (timer !== undefined) {
      this.cancelTimeout(timer);
      this.iceRetryTimers.delete(peerId);
    }
    this.iceRetryAttempts.delete(peerId);
  }

  private clearAllIceRetries(): void {
    for (const timer of this.iceRetryTimers.values()) {
      this.cancelTimeout(timer);
    }
    this.iceRetryTimers.clear();
    this.iceRetryAttempts.clear();
  }

  /** Ensure a peer connection exists; lower id is polite (Perfect Negotiation). */
  ensurePeer(peerId: string): void {
    if (!peerId || peerId === this.selfId) return;

    // State A: never construct PC
    if (this.phase === "pre-start") {
      this.pendingPeerIds.add(peerId);
      return;
    }

    // State B or C
    let peer = this.peers.get(peerId);
    if (!peer) {
      peer = this.createPeerMedia(peerId);
      this.peers.set(peerId, peer);
    }
    // Primary fix: always re-sync (no-op in B if localStream null; attaches in C / after gUM)
    this.syncLocalTracksToPeer(peer);
  }

  async handleSignal(from: string, data: unknown): Promise<void> {
    if (!data || typeof data !== "object") return;

    if (this.phase === "pre-start") {
      this.pendingPeerIds.add(from);
      this.enqueueSignal(from, data);
      return;
    }

    // State B or C: process immediately
    this.ensurePeer(from);
    const peer = this.peers.get(from);
    if (!peer) return;
    const { pc } = peer;

    const msg = data as {
      kind?: string;
      description?: RTCSessionDescriptionInit;
      candidate?: RTCIceCandidateInit;
    };

    if (msg.kind === "sdp" && msg.description) {
      const description = msg.description;
      const offerCollision =
        description.type === "offer" && (peer.makingOffer || pc.signalingState !== "stable");
      peer.ignoreOffer = !peer.polite && offerCollision;
      if (peer.ignoreOffer) return;

      try {
        // stop()/removePeer may close the PC mid-flight (void handleSignal from hook).
        await pc.setRemoteDescription(description);
        if (!this.peers.has(from)) return;
        if (description.type === "offer") {
          await pc.setLocalDescription();
          if (!this.peers.has(from)) return;
          this.cb.sendSignal(from, { kind: "sdp", description: pc.localDescription });
        }
      } catch (err) {
        if (!this.peers.has(from)) return;
        logger.warn("webrtc_handle_sdp_failed", {
          from,
          signalingState: pc.signalingState,
          errMessage: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }

    if (msg.kind === "ice" && msg.candidate) {
      try {
        await pc.addIceCandidate(msg.candidate);
      } catch {
        if (!peer.ignoreOffer && this.peers.has(from)) {
          // swallow ICE errors during ignore / closed-PC races
        }
      }
    }
  }

  removePeer(peerId: string): void {
    // Always clear State A bookkeeping so a left peer is never flushed later.
    this.pendingPeerIds.delete(peerId);
    this.signalBuffer = this.signalBuffer.filter((s) => s.from !== peerId);
    // Cancel any pending silent ICE re-ensure (peer-left or explicit teardown).
    // handlePeerConnectionFailed re-schedules *after* this returns.
    this.clearIceRetry(peerId);

    const peer = this.peers.get(peerId);
    if (!peer) return;
    try {
      peer.pc.close();
    } catch {
      // ignore
    }
    if (peer.audioSource) {
      try {
        peer.audioSource.disconnect();
      } catch {
        // ignore
      }
    }
    try {
      peer.audioGain.disconnect();
    } catch {
      // ignore
    }
    this.peers.delete(peerId);
    this.cb.onRemoteStreamGone?.(peerId);
  }

  /** Attach / update a video element for a remote peer's stream. */
  bindVideoElement(peerId: string, el: HTMLVideoElement | null): void {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    peer.videoEl = el;
    if (el) {
      el.srcObject = peer.remoteStream;
      el.muted = true; // audio via Web Audio spatial gain
      el.playsInline = true;
      void el.play().catch(() => {
        // autoplay may require gesture; office interaction unlocks later
      });
    }
  }

  private tick = (): void => {
    this.raf = requestAnimationFrame(this.tick);
    if (this.audioCtx?.state === "suspended") {
      void this.audioCtx.resume();
    }
    for (const [id, peer] of this.peers) {
      const vol = this.volumeOf(id);
      // Smooth-ish gain; mute hard at zero for private rooms.
      const target = vol <= 0.02 ? 0 : vol * vol; // square for natural falloff
      peer.audioGain.gain.value = target;
      if (peer.videoEl) {
        peer.videoEl.style.opacity = String(0.35 + 0.65 * Math.min(1, vol));
      }
    }
  };

  stop(): void {
    // Invalidate any in-flight start() continuations before tearing media down.
    this.startEpoch += 1;

    cancelAnimationFrame(this.raf);
    this.raf = 0;
    // Clear ICE recovery timers first so removePeer loops don't race re-ensure.
    this.clearAllIceRetries();
    for (const id of [...this.peers.keys()]) this.removePeer(id);

    const wasSharing = this.sharing;
    if (this.screenTrack) {
      this.screenTrack.removeEventListener("ended", this.onScreenTrackEnded);
      this.screenTrack.stop();
      this.screenTrack = null;
    }
    if (this.parkedCameraTrack) {
      this.parkedCameraTrack.stop();
      this.parkedCameraTrack = null;
    }
    this.sharing = false;

    if (this.localStream) {
      for (const t of this.localStream.getTracks()) t.stop();
      this.localStream = null;
    }
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
    this.masterGain = null;
    this.pendingPeerIds.clear();
    this.signalBuffer = [];
    this.phase = "pre-start";
    this.selfId = "";
    this.micEnabled = true;
    this.camEnabled = true;
    this.volumeOf = () => 0;
    if (wasSharing) {
      this.cb.onSharingChange?.(false);
    }
    this.notifyLocalStream();
  }
}
